import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus,
  FileEdit,
  Filter,
  Search,
  Loader2,
  Clock,
  Calendar,
  User,
  MoreHorizontal,
  Check,
  Trash2,
  Eye,
  Download,
  Grid3X3,
  List,
  Share2,
  Users,
  UserCheck,
  RefreshCw,
  FileSearch,
  ArrowRight,
  FileText,
  FileDown,
  FileCode,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ShieldCheck,
  ShieldAlert,
} from "lucide-react";
import { buildBrandColors, exportPlatePagesToPuppeteerPdf, exportPlatePagesToDocx, exportPlatePagesToHtml } from "../utils/exportPlate";
import { BrandingService } from "@foundation/services/brandingService";
import { fetchEditorContent, fetchSavedContent, getApprovalState } from "../services/boostPolicyService";
import { usePolicyApproval, isApprovalGatePassed } from "../hooks/usePolicyApproval";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@foundation/components/ui/card";
import { Button } from "@foundation/components/ui/button";
import { Input } from "@foundation/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@foundation/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@foundation/components/ui/dropdown-menu";
import { Badge } from "@foundation/components/ui/badge";
import { useToast } from "@foundation/hooks/use-toast";
import { useUser } from "@foundation/contextapi/UserContext";
import { getActiveWorkspaceId } from "../../../header/activeWorkspace";
import { getSharedDocuments } from "../services/documentSharingService";

import RevisrLayout from "../components/RevisrLayout";

// Import sharing functionality
import { DocumentSharingModal } from "../components/sharing/DocumentSharingModal";

interface ScopingDocument {
  id: string;
  title: string;
  status: string;
  client_id?: string;
  created_at: string;
  updated_at: string;
  client_name?: string;
  markdown_url?: string;
  policy_markdown_url?: string;
  current_step?: number; // For resume workflow (1-4)
  org_id?: string; // Organization ID for API calls during resume
  workflow_type?: string; // 'create_policy' or 'review_policy'
  // Shared document specific fields
  is_shared?: boolean;
  shared_by_name?: string;
  permission_level?: string;
  shared_at?: string;
  expires_at?: string;
  share_source?: "direct" | "team" | "organisation";
  share_source_name?: string;
  entity_type?: string;
  // Approval workflow: metadata.approval holds { status: 'pending'|'approved'|'rejected', ... }
  metadata?: Record<string, unknown> | null;
}

// Page-size options for the policy list. Default keeps the page light; the
// pagination bar only appears once the filtered set spills past one page
// (e.g. when a user has created more than 100 documents).
const PAGE_SIZE_OPTIONS = [12, 24, 48, 96] as const;
const DEFAULT_PAGE_SIZE = 12;

// Build a compact page-number sequence with leading/trailing ellipses so the
// control stays neat regardless of total page count (e.g. 1 … 4 5 6 … 20).
function buildPageItems(current: number, total: number): (number | "ellipsis")[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const delta = 1;
  const left = Math.max(2, current - delta);
  const right = Math.min(total - 1, current + delta);
  const items: (number | "ellipsis")[] = [1];
  if (left > 2) items.push("ellipsis");
  for (let i = left; i <= right; i++) items.push(i);
  if (right < total - 1) items.push("ellipsis");
  items.push(total);
  return items;
}

export default function PolicyDocuments() {
  const navigate = useNavigate();
  const { user, isLoading: userIsLoading } = useUser();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [documentTypeFilter, setDocumentTypeFilter] = useState("all"); // New filter for owned vs shared
  const [proposals, setProposals] = useState<ScopingDocument[]>([]);
  const [sharedProposals, setSharedProposals] = useState<ScopingDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("list");
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Approval workflow: members (non-org-admins) cannot export a policy until it's
  // approved. Org admins / flag-off → no gate. Used to gate the export menu items
  // and render an approval badge next to the policy title.
  const { approvalRequired, isOrgAdmin } = usePolicyApproval();
  const [workspaceTrigger, setWorkspaceTrigger] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const { toast } = useToast();

  // Sharing state
  const [isSharingModalOpen, setIsSharingModalOpen] = useState(false);
  const [selectedDocumentForSharing, setSelectedDocumentForSharing] = useState<ScopingDocument | null>(null);

  // Export state — tracks which doc is currently being exported and in which format
  const [exportingDoc, setExportingDoc] = useState<{ id: string; format: 'pdf' | 'word' | 'html' } | null>(null);

  const handleExportDoc = useCallback(async (doc: ScopingDocument, format: 'pdf' | 'word' | 'html') => {
    setExportingDoc({ id: doc.id, format });
    try {
      // Your Policies has no live editor, so fetch the branded editor pages saved
      // in the DB (editor_content_url JSON) — byte-identical to what the boost
      // editor exports. Fall back to the generation-time resolved pages for older
      // policies that were generated but never opened/saved in the editor.
      let pages: Parameters<typeof exportPlatePagesToPuppeteerPdf>[0] | null = null;

      const editor = await fetchEditorContent(doc.id).catch(() => null);
      if (editor && Array.isArray(editor.pages) && editor.pages.length > 0) {
        pages = editor.pages as Parameters<typeof exportPlatePagesToPuppeteerPdf>[0];
      } else {
        const saved = await fetchSavedContent(doc.id);
        if (saved && Array.isArray(saved.resolvedPages) && saved.resolvedPages.length > 0) {
          pages = saved.resolvedPages as Parameters<typeof exportPlatePagesToPuppeteerPdf>[0];
        }
      }

      if (!pages || pages.length === 0) {
        toast({ title: 'No content found', description: 'This policy has no exportable content yet.', variant: 'destructive' });
        return;
      }

      const fileName = doc.title || 'Policy';

      // Fetch org branding so the export matches the PlateJS editor's output.
      let branding = null;
      let orgName: string | undefined;
      if (doc.org_id) {
        try {
          const brandingData = await BrandingService.getOrganizationBranding(doc.org_id);
          if (brandingData) {
            orgName = brandingData.brand_title ?? undefined;
            branding = {
              primary_color:   brandingData.primary_color   ?? undefined,
              secondary_color: brandingData.secondary_color ?? undefined,
              font_color:      brandingData.font_color      ?? undefined,
              font_family:     brandingData.font_family     ?? undefined,
              logo_url:        brandingData.logo_url        ?? undefined,
            };
          }
        } catch {
          // Non-fatal — fall back to default brand colors
        }
      }

      const colors = buildBrandColors(branding);
      const exportDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
      const footerMeta = { policyName: doc.title || undefined, orgName, date: exportDate };

      if (format === 'pdf') {
        await exportPlatePagesToPuppeteerPdf(pages, fileName, colors, footerMeta);
      } else if (format === 'word') {
        await exportPlatePagesToDocx(pages, fileName, colors, footerMeta);
      } else {
        await exportPlatePagesToHtml(pages, fileName, colors, footerMeta);
      }
    } catch (err) {
      console.error('[PolicyDocuments] export failed:', err);
      toast({ title: 'Export failed', description: 'Could not export the policy. Please try again.', variant: 'destructive' });
    } finally {
      setExportingDoc(null);
    }
  }, [toast]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!userIsLoading && !user) {
      navigate("/login");
    }
  }, [user, userIsLoading, navigate]);

  // Re-fetch when workspace changes
  useEffect(() => {
    const handleWorkspaceChanged = () => setWorkspaceTrigger(t => t + 1);
    window.addEventListener('workspace-changed', handleWorkspaceChanged);
    return () => window.removeEventListener('workspace-changed', handleWorkspaceChanged);
  }, []);

  // Fetch documents from Supabase - both owned and shared
  useEffect(() => {
    const fetchDocuments = async () => {
      // Skip if still loading auth or no user
      if (userIsLoading || !user) {
        return;
      }

      setIsLoading(true);

      try {
        // Use the user ID from @base context instead of direct supabase call
        const userId = user.id;
        if (!userId) {
          console.error("No user ID found");
          return;
        }

        const orgId = getActiveWorkspaceId() ?? undefined;
        let ownedData: any[] = [];
        try {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore: import.meta.env is provided by Vite at build time
          const apiUrl = (import.meta.env?.VITE_API_URL as string) || 'http://localhost:3001';
          const res = await fetch(`${apiUrl}/api/revisr/policy-generation`, {
            headers: { 'Content-Type': 'application/json', ...(orgId ? { 'x-org-id': orgId } : {}) },
          });
          if (res.ok) {
            const data = await res.json() as { records: any[] };
            ownedData = (data.records ?? []).sort((a: any, b: any) => {
              const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
              const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
              return dateB - dateA;
            });
          }
        } catch (fetchError) {
          console.error("Error fetching policies:", fetchError);
        }

        // Fetch shared documents (if sharing is configured for policies)
        const sharedData = await getSharedDocuments();

        // Transform policies from Teable to match ScopingDocument interface
        const transformedOwned = (ownedData || []).map((doc: any) => ({
          id: doc.id,
          title: doc.policy_name || 'Untitled',
          status: doc.status || "completed",
          current_step: doc.current_step || 4,
          created_at: doc.created_at || new Date().toISOString(),
          updated_at: doc.created_at || new Date().toISOString(),
          client_name: "Policy Document",
          markdown_url: doc.markdown_url,
          policy_markdown_url: doc.policy_markdown_url,
          org_id: doc.org_id,
          workflow_type: doc.workflow_type || 'review_policy',
          metadata: doc.metadata,
          is_shared: false,
        }));

        // Get owned document IDs to prevent duplicates
        const ownedDocumentIds = new Set(
          (ownedData || []).map((doc: any) => doc.id)
        );

        // Transform shared documents, excluding ones the user owns
        const transformedShared = sharedData
          .filter((doc: any) => !ownedDocumentIds.has(doc.document_id)) // Exclude owned documents
          .map((doc: any) => ({
            id: doc.document_id,
            title: doc.title,
            status: doc.status || "draft",
            client_name: doc.client_name || "Unknown Client",
            created_at: doc.shared_at, // Use shared date for sorting
            updated_at: doc.shared_at,
            is_shared: true,
            shared_by_name: doc.shared_by_name,
            permission_level: doc.permission_level,
            shared_at: doc.shared_at,
            expires_at: doc.expires_at,
            share_source: doc.share_source,
            share_source_name: doc.share_source_name,
            entity_type: doc.entity_type,
          }));

        setProposals(transformedOwned);
        setSharedProposals(transformedShared);
      } catch (error) {
        console.error("Error in fetchDocuments:", error);
        toast({
          title: "Error",
          description: "An unexpected error occurred",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocuments();
  }, [user?.id, userIsLoading, toast, workspaceTrigger]);

  // Refresh when user returns from policy generation/editing
  useEffect(() => {
    const refreshPolicies = async () => {
      const isStale = sessionStorage.getItem('scopingai_proposals_stale');
      if (isStale !== 'true') return;
      sessionStorage.removeItem('scopingai_proposals_stale');

      if (!user?.id || userIsLoading) return;

      setIsLoading(true);
      try {
        const refreshOrgId = getActiveWorkspaceId() ?? undefined;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore: import.meta.env is provided by Vite at build time
        const apiUrl = (import.meta.env?.VITE_API_URL as string) || 'http://localhost:3001';
        const res = await fetch(`${apiUrl}/api/revisr/policy-generation`, {
          headers: { 'Content-Type': 'application/json', ...(refreshOrgId ? { 'x-org-id': refreshOrgId } : {}) },
        });
        if (res.ok) {
          const data = await res.json() as { records: any[] };
          const refreshed = (data.records ?? [])
            .sort((a: any, b: any) => {
              const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
              const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
              return dateB - dateA;
            })
            .map((doc: any) => ({
              id: doc.id,
              title: doc.policy_name || 'Untitled',
              status: doc.status || 'completed',
              current_step: doc.current_step || 4,
              created_at: doc.created_at || new Date().toISOString(),
              updated_at: doc.updated_at || doc.created_at || new Date().toISOString(),
              client_name: 'Policy Document',
              markdown_url: doc.markdown_url,
              policy_markdown_url: doc.policy_markdown_url,
              org_id: doc.org_id,
              workflow_type: doc.workflow_type || 'review_policy',
              metadata: doc.metadata,
              is_shared: false,
            }));
          setProposals(refreshed);
        }
      } catch (error) {
        console.error('Error refreshing policies:', error);
      } finally {
        setIsLoading(false);
      }
    };

    refreshPolicies();

    const handleFocus = () => refreshPolicies();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user?.id, userIsLoading]);

  // Manual + focus-triggered refresh of the member's own policies. Uses a light
  // refetch (no isLoading flash) so the list stays visible while the Refresh
  // button spins. This catches approval-state changes made by an org admin in
  // another session when the user returns to the tab. No page reload.
  const refreshDocuments = useCallback(async () => {
    if (!user?.id) return;
    setIsRefreshing(true);
    try {
      const orgId = getActiveWorkspaceId() ?? undefined;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore: import.meta.env is provided by Vite at build time
      const apiUrl = (import.meta.env?.VITE_API_URL as string) || 'http://localhost:3001';
      const res = await fetch(`${apiUrl}/api/revisr/policy-generation`, {
        headers: { 'Content-Type': 'application/json', ...(orgId ? { 'x-org-id': orgId } : {}) },
      });
      if (res.ok) {
        const data = await res.json() as { records: any[] };
        const transformed = (data.records ?? []).map((doc: any) => ({
          id: doc.id,
          title: doc.policy_name || 'Untitled',
          status: doc.status || 'completed',
          current_step: doc.current_step || 4,
          created_at: doc.created_at || new Date().toISOString(),
          updated_at: doc.created_at || new Date().toISOString(),
          client_name: 'Policy Document',
          markdown_url: doc.markdown_url,
          policy_markdown_url: doc.policy_markdown_url,
          org_id: doc.org_id,
          workflow_type: doc.workflow_type || 'review_policy',
          metadata: doc.metadata,
          is_shared: false,
        }));
        setProposals(transformed);
      }
    } catch (err) {
      console.error('[PolicyDocuments] refresh failed:', err);
    } finally {
      setIsRefreshing(false);
    }
  }, [user?.id]);

  useEffect(() => {
    const onFocus = () => refreshDocuments();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshDocuments]);

  // Combine and filter documents based on search query, status, and document type
  const allDocuments = [...proposals, ...sharedProposals];
  const filteredProposals = allDocuments.filter((proposal) => {
    const matchesSearch =
      proposal.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      proposal.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (proposal.shared_by_name &&
        proposal.shared_by_name
          .toLowerCase()
          .includes(searchQuery.toLowerCase())) ||
      (proposal.share_source_name &&
        proposal.share_source_name
          .toLowerCase()
          .includes(searchQuery.toLowerCase()));

    const matchesStatus =
      statusFilter === "all" ||
      proposal.status.toLowerCase() === statusFilter.toLowerCase();

    const matchesDocumentType =
      documentTypeFilter === "all" ||
      (documentTypeFilter === "owned" && !proposal.is_shared) ||
      (documentTypeFilter === "shared" && proposal.is_shared);

    return matchesSearch && matchesStatus && matchesDocumentType;
  });

  // Sort by created date (or shared date for shared documents)
  const sortedProposals = filteredProposals.sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  // Pagination — slice the sorted set into the current page.
  const totalItems = sortedProposals.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);
  const paginatedProposals = sortedProposals.slice(startIndex, endIndex);

  // Reset to the first page whenever the result set changes (search/filter/page-size).
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, documentTypeFilter, pageSize]);

  // Clamp the active page if the total shrinks below it (e.g. after a delete).
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Get permission level display
  const getPermissionDisplay = (level?: string) => {
    if (!level) return "";
    switch (level) {
      case "view":
        return "View Only";
      case "edit":
        return "Can Edit";
      default:
        return level;
    }
  };

  // Get permission color
  const getPermissionColor = (level?: string) => {
    switch (level) {
      case "view":
        return "bg-blue-100 text-blue-800";
      case "edit":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Get share source icon
  const getShareSourceIcon = (source?: string) => {
    switch (source) {
      case "direct":
        return <UserCheck className="h-3 w-3" />;
      case "team":
        return <Users className="h-3 w-3" />;
      case "organisation":
        return <Users className="h-3 w-3" />;
      default:
        return <Share2 className="h-3 w-3" />;
    }
  };

  // Get share source display text
  const getShareSourceDisplay = (source?: string, sourceName?: string) => {
    switch (source) {
      case "direct":
        return "Direct Share";
      case "team":
        return `Team: ${sourceName || "Unknown"}`;
      case "organisation":
        return `Org: ${sourceName || "Unknown"}`;
      default:
        return "Shared";
    }
  };

  // Delete a document (only for owned documents)
  const handleDeleteDocument = async (id: string, isShared: boolean) => {
    if (isShared) {
      toast({
        title: "Cannot Delete",
        description:
          "You cannot delete shared documents. Contact the owner to revoke access.",
        variant: "destructive",
      });
      return;
    }

    try {
      // First ask for confirmation
      if (!confirm("Are you sure you want to delete this document? This will also remove all associated files.")) {
        return;
      }

      // Find the document to get org_id
      const doc = proposals.find((d) => d.id === id);
      const orgId = doc?.org_id || '';

      // Use the DELETE endpoint for policy review
      const response = await fetch(`/api/revisr/policy-review/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': user?.id || '',
          'x-org-id': orgId,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete document');
      }

      toast({
        title: "Success",
        description: "Document and associated files deleted successfully",
      });

      // Remove the deleted document from the state
      setProposals((prev) => prev.filter((doc) => doc.id !== id));
    } catch (error) {
      console.error("Error deleting document:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete document",
        variant: "destructive",
      });
    }
  };

  // 🚨 NEW: Handle regenerate document from proposals page
  const handleRegenerateDocument = async (doc: any) => {
    try {
      console.log("🔄 Preparing regeneration for document:", doc.title);
      console.log("🔍 Document data:", doc);

      // 🚨 FIX: Prepare data structure that DocumentsRegeneration.tsx expects
      const regenerationData = {
        // DocumentsRegeneration.tsx expects these exact keys (line 140-180)
        document: {
          id: doc.id,
          title: doc.title,
          content: doc.content || doc.markdown_content || "",
        },
        selectedDocument: {
          id: doc.id,
          title: doc.title,
          content: doc.content || doc.markdown_content || "",
        },
        // Client data from the existing document
        client: {
          id: doc.client_id || null,
          name: doc.client_name || "",
          email: doc.client_email || "",
          company: doc.client_name || "",
          description: "",
        },
        // Project data from the existing document
        project: {
          title: doc.title || "",
          description: doc.description || "",
          objectives: [],
          timeline: "",
          budget: "",
        },
        selectedKnowledgeDocuments: [],
        documentRequirements: {},
        aiPrompts: {},
        // 🚨 IMPORTANT: Mark as coming from proposals page (not auto-trigger)
        fromProposalsPage: true,
        regenerationMode: true,
        timestamp: Date.now(),
      };

      // Store in sessionStorage for DocumentsRegeneration.tsx
      sessionStorage.setItem(
        "scopingai_proposal_data",
        JSON.stringify(regenerationData)
      );

      // 🚨 FIX: NO auto-trigger flag - user should control regeneration
      // sessionStorage.setItem('autoTriggerRegeneration', 'true'); // REMOVED

      console.log("🚨 REGENERATION DATA PREPARED - Navigating to regeneration screen (NO AUTO-TRIGGER)");
      console.log("🚨 Data structure:", regenerationData);
      console.log("🚨 CRITICAL DEBUG - Document info being passed:", {
        documentId: doc.id,
        documentTitle: doc.title,
        fromProposalsPage: regenerationData.fromProposalsPage,
        hasDocument: !!regenerationData.document,
        hasSelectedDocument: !!regenerationData.selectedDocument,
        documentInData: regenerationData.document,
        selectedDocumentInData: regenerationData.selectedDocument
      });

      // Navigate to regeneration screen WITHOUT auto-trigger
      navigate(`/revisr/documents/regeneration`);

    } catch (error) {
      console.error("❌ Error preparing regeneration:", error);
      toast({
        title: "Error",
        description: "Failed to prepare document for regeneration",
        variant: "destructive",
      });
    }
  };





  // Share document handler
  const handleShareDocument = (document: ScopingDocument) => {
    setSelectedDocumentForSharing(document);
    setIsSharingModalOpen(true);
  };

  // Navigation handler for Review Existing Policy workflow
  const handleReviewExistingPolicy = () => {
    // Clear any previous workflow data
    sessionStorage.removeItem('auditComparisonData');
    sessionStorage.removeItem('selectedIndustry');
    sessionStorage.removeItem('selectedTemplate');
    sessionStorage.removeItem('selectedReference');
    sessionStorage.removeItem('detectedChanges');
    sessionStorage.removeItem('reviewedChanges');
    sessionStorage.removeItem('documentContent');
    sessionStorage.removeItem('finalDocumentContent');
    sessionStorage.removeItem('policy_review_id');

    // Set workflow type to review_policy
    sessionStorage.setItem('workflow_type', 'review_policy');

    // Navigate to NewAudit page (first step in review workflow)
    navigate("/revisr/new-audit");
  };

  // Handle clicking on a document - for draft documents, resume workflow
  const handleDocumentClick = async (doc: ScopingDocument) => {
    // boost_policy routing is always step-based — check before status-based early return
    if (doc.workflow_type === 'boost_policy') {
      const currentStep = doc.current_step ?? 0;
      if (currentStep >= 6) {
        navigate(`/revisr/brand-policy/preview?tab=preview&id=${doc.id}`);
      } else if (currentStep >= 5) {
        navigate(`/revisr/brand-policy/generating?id=${doc.id}`);
      } else {
        // Maps DB current_step → URL step name for the current 4-step flow
        // (library → setup → qa → review). Keep aligned with STEP_NAME_TO_INDEX
        // in types/boostPolicy.ts. current_step 3 is a legacy value mapped to review.
        const stepNames: Record<number, string> = {
          0: 'library',
          1: 'setup',
          2: 'qa',
          3: 'review',
          4: 'review',
        };
        const stepName = stepNames[currentStep] || 'library';
        navigate(`/revisr/boost-policy?step=${stepName}&id=${doc.id}`);
      }
      toast({ title: "Resuming Boost Policy", description: "Returning to where you left off." });
      return;
    }

    // For completed non-boost_policy documents, navigate to generated view
    if (doc.status === 'completed') {
      navigate(`/revisr/policies/generated?id=${doc.id}`);
      return;
    }

    // Handle create_policy workflow resume
    if (doc.workflow_type === 'create_policy') {
      const currentStep = doc.current_step || 2;

      // Set create_policy session data
      sessionStorage.setItem('create_policy_policy_review_id', doc.id);
      if (doc.org_id) {
        sessionStorage.setItem('orgId', doc.org_id);
      }

      const createPolicyStepRoutes: Record<number, string> = {
        1: '/revisr/create-policy',
        2: `/revisr/create-policy/generated?id=${doc.id}`,
        3: `/revisr/create-policy/edit?id=${doc.id}`,
        4: `/revisr/create-policy/export?id=${doc.id}`,
      };

      const route = createPolicyStepRoutes[currentStep] || `/revisr/create-policy/generated?id=${doc.id}`;

      toast({
        title: "Resuming Policy Creation",
        description: `Returning to step ${currentStep} of the create policy workflow.`,
      });

      navigate(route);
      return;
    }

    // For review_policy draft documents, resume the workflow at the correct step
    // First, clear any stale session data
    sessionStorage.removeItem('auditComparisonData');
    sessionStorage.removeItem('detectedChanges');
    sessionStorage.removeItem('reviewedChanges');
    sessionStorage.removeItem('documentContent');
    sessionStorage.removeItem('auditResults');
    sessionStorage.removeItem('finalDocumentContent');

    // Store review ID and workflow type immediately
    sessionStorage.setItem('policy_review_id', doc.id);
    sessionStorage.setItem('workflow_type', 'review_policy');

    // For draft documents, fetch the policy review to get current step and all data
    try {
      const response = await fetch(`/api/revisr/policy-review/${doc.id}`, {
        headers: {
          'x-user-id': user?.id || '',
          'x-org-id': doc.org_id || '', // Use org_id from the document
        },
      });

      if (!response.ok) {
        // If API fails, use the current_step from the document if available
        const currentStep = doc.current_step || 1;
        // For step 4, include the document ID in the URL
        const stepRoutes: Record<number, string> = {
          1: '/revisr/new-audit',
          2: '/revisr/scan-review',
          3: '/revisr/edit-approve',
          4: `/revisr/policies/generated?id=${doc.id}`,
        };
        const route = stepRoutes[currentStep] || '/revisr/new-audit';

        // Store orgId in session for the workflow pages
        if (doc.org_id) {
          sessionStorage.setItem('orgId', doc.org_id);
        }

        // Store minimal auditComparisonData even when API fails
        // This ensures ScanAndReview.tsx has the data it needs
        const fallbackAuditData = {
          storeId: null,
          policyFileName: doc.title,
          standardFileName: '', // Will need to be re-selected
          conversionSource: 'resume-fallback',
        };
        sessionStorage.setItem('auditComparisonData', JSON.stringify(fallbackAuditData));

        toast({
          title: "Resuming Review",
          description: `Returning to step ${currentStep} of the review workflow.`,
        });
        navigate(route);
        return;
      }

      const reviewData = await response.json();

      // Store org ID in session for the workflow pages
      sessionStorage.setItem('orgId', reviewData.org_id || '');

      // Store audit comparison data for resuming
      const auditComparisonData = {
        storeId: reviewData.policy_store_id || null,
        policyFileName: reviewData.policy_name || doc.title,
        standardFileName: '', // Will be loaded from the store
        conversionSource: 'resume',
      };
      sessionStorage.setItem('auditComparisonData', JSON.stringify(auditComparisonData));

      // Fetch the document content if policy_markdown_url exists
      if (reviewData.policy_markdown_url) {
        try {
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

          // Fetch markdown content from storage
          const contentResponse = await fetch(`${supabaseUrl}/storage/v1/object/public/knowledge_docs/${reviewData.policy_markdown_url}`, {
            headers: {
              'apikey': supabaseKey,
              'Authorization': `Bearer ${supabaseKey}`,
            },
          });

          if (contentResponse.ok) {
            const markdownContent = await contentResponse.text();
            sessionStorage.setItem('documentContent', markdownContent);
            sessionStorage.setItem('policyMarkdownUrl', reviewData.policy_markdown_url);
          }
        } catch (contentError) {
          console.warn('Could not fetch document content:', contentError);
        }
      }

      // If there are saved changes (json_changes_url), fetch and store them
      if (reviewData.json_changes_url) {
        try {
          const changesResponse = await fetch(`/api/revisr/policy-review/${doc.id}/changes`, {
            headers: {
              'x-user-id': user?.id || '',
              'x-org-id': reviewData.org_id || '',
            },
          });

          if (changesResponse.ok) {
            const { changes, audit_results } = await changesResponse.json();
            if (changes && changes.length > 0) {
              sessionStorage.setItem('detectedChanges', JSON.stringify(changes));
            }
            if (audit_results) {
              sessionStorage.setItem('auditResults', JSON.stringify(audit_results));
            }
          }
        } catch (changesError) {
          console.warn('Could not fetch saved changes:', changesError);
        }
      }

      // Navigate to the appropriate step based on current_step from database
      // For step 4, include the document ID in the URL
      const stepRoutes: Record<number, string> = {
        1: '/revisr/new-audit',
        2: '/revisr/scan-review',
        3: '/revisr/edit-approve',
        4: `/revisr/policies/generated?id=${doc.id}`,
      };

      const currentStep = reviewData.current_step || 1;
      const route = stepRoutes[currentStep] || '/revisr/new-audit';

      toast({
        title: "Resuming Review",
        description: `Returning to step ${currentStep} of the review workflow.`,
      });

      navigate(route);
    } catch (error) {
      console.warn('Could not fetch policy review details:', error);
      // Even on error, navigate to the step from doc.current_step if available
      const currentStep = doc.current_step || 1;
      // For step 4, include the document ID in the URL
      const stepRoutes: Record<number, string> = {
        1: '/revisr/new-audit',
        2: '/revisr/scan-review',
        3: '/revisr/edit-approve',
        4: `/revisr/policies/generated?id=${doc.id}`,
      };
      const route = stepRoutes[currentStep] || '/revisr/new-audit';

      // Store orgId in session for the workflow pages
      if (doc.org_id) {
        sessionStorage.setItem('orgId', doc.org_id);
      }

      // Store minimal auditComparisonData even when API throws error
      // This ensures ScanAndReview.tsx has the data it needs
      const fallbackAuditData = {
        storeId: null,
        policyFileName: doc.title,
        standardFileName: '', // Will need to be re-selected
        conversionSource: 'resume-error-fallback',
      };
      sessionStorage.setItem('auditComparisonData', JSON.stringify(fallbackAuditData));

      toast({
        title: "Resuming Review",
        description: `Returning to step ${currentStep} of the review workflow.`,
      });
      navigate(route);
    }
  };

  // Show loading state while checking authentication — keep RevisrLayout so sidebar doesn't flash in/out
  if (userIsLoading) {
    return (
      <RevisrLayout>
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </RevisrLayout>
    );
  }

  return (
    <RevisrLayout>
      <div className="container mx-auto px-4 py-5 md:px-6 md:py-8 overflow-x-hidden bg-background">
        <div className="space-y-5 md:space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">Your Policies</h1>
              <p className="text-muted-foreground mt-1 text-sm">
                Manage all your policies in one place
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={refreshDocuments}
                disabled={isRefreshing}
                className="gap-1.5 min-h-[36px]"
                title={isRefreshing ? "Refreshing..." : "Refresh policies"}
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
                <span className="hidden sm:inline">{isRefreshing ? "Refreshing..." : "Refresh"}</span>
              </Button>
              <div className="flex items-center gap-1 border rounded-md p-0.5">
                <Button
                  variant={viewMode === "grid" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("grid")}
                  className="min-h-[36px] min-w-[36px]"
                  data-testid="view-grid"
                >
                  <Grid3X3 className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === "list" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("list")}
                  className="min-h-[36px] min-w-[36px]"
                  data-testid="view-list"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
              <Button
                onClick={() => navigate("/revisr/boost-policy")}
                className="gap-2 group flex-1 sm:flex-none justify-center"
              >
                <Plus className="h-4 w-4" />
                {proposals.length > 0 ? "Boost your Policy" : "Create First Policy"}
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>

          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search policies..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <div className="w-full sm:w-48">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <div className="flex items-center gap-2">
                      <Filter className="h-4 w-4" />
                      <SelectValue placeholder="Filter by Status" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Policy Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <FileEdit className="h-5 w-5 text-primary" />
                  <div>
                    <p className="text-sm font-medium">My Policies</p>
                    <p className="text-2xl font-bold">{proposals.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-green-500" />
                  <div>
                    <p className="text-sm font-medium">Completed Policies</p>
                    <p className="text-2xl font-bold">{proposals.filter(p => p.status === 'completed' || p.status === 'created').length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Document List */}
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2">Loading policies...</span>
            </div>
          ) : sortedProposals.length === 0 ? (
            <Card className="w-full">
              <CardContent className="p-6 text-center">
                <div className="flex flex-col items-center justify-center py-10 space-y-4">
                  <FileEdit size={48} className="text-muted-foreground" />
                  <h3 className="text-lg font-medium">No policies found</h3>
                  <p className="text-muted-foreground max-w-md">
                    {searchQuery || statusFilter !== "all"
                      ? "Try adjusting your search or filter to find what you're looking for."
                      : "Get started by creating your first policy."}
                  </p>
                  <Button
                    onClick={() => navigate("/revisr/boost-policy")}
                    className="gap-2 group"
                  >
                    <Plus className="h-4 w-4" />
                    {proposals.length > 0 ? "Get Started" : "Create First Policy"}
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : viewMode === "grid" ? (
            // Grid View
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {paginatedProposals.map((proposal) => (
                <Card
                  key={proposal.id}
                  className="transition-shadow"
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg flex items-center gap-2">
                          {proposal.title}
                          {proposal.is_shared && (
                            <Share2 className="h-4 w-4 text-blue-500" />
                          )}
                        </CardTitle>
                        {proposal.is_shared && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Shared by {proposal.shared_by_name}
                          </p>
                        )}
                        {proposal.is_shared && proposal.share_source && (
                          <div className="flex items-center gap-1 text-xs text-blue-600 mt-1">
                            {getShareSourceIcon(proposal.share_source)}
                            {getShareSourceDisplay(
                              proposal.share_source,
                              proposal.share_source_name
                            )}
                          </div>
                        )}
                      </div>
                      {(() => {
                        // Approval-aware status pill for boost policies. The approval
                        // decision (pending/rejected/approved) is shown to EVERY viewer
                        // (including org admins, who manage approvals) instead of the raw
                        // DB status, so a pending policy never reads as "Completed".
                        if (proposal.workflow_type === 'boost_policy') {
                          const state = getApprovalState(proposal);
                          if (state === 'pending') {
                            return (
                              <div className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 inline-flex items-center gap-1">
                                <Clock className="h-3 w-3" /> Pending approval
                              </div>
                            );
                          }
                          if (state === 'rejected') {
                            return (
                              <div className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 inline-flex items-center gap-1">
                                <ShieldAlert className="h-3 w-3" /> Rejected
                              </div>
                            );
                          }
                          if (state === 'approved') {
                            return (
                              <div className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 inline-flex items-center gap-1">
                                <ShieldCheck className="h-3 w-3" /> Approved
                              </div>
                            );
                          }
                        }
                        // Fallback: map the raw DB status to a readable label + tone.
                        const raw = proposal.status || "draft";
                        const label =
                          raw === "pending_approval" ? "Pending approval"
                          : raw === "rejected" ? "Rejected"
                          : raw === "generated" ? "Generated"
                          : raw === "completed" || raw === "created" ? "Completed"
                          : raw === "draft" ? "Draft"
                          : raw.charAt(0).toUpperCase() + raw.slice(1);
                        const tone =
                          raw === "completed" || raw === "created" ? "bg-green-100 text-green-800"
                          : raw === "draft" || raw === "pending_approval" ? "bg-amber-100 text-amber-800"
                          : raw === "rejected" ? "bg-red-100 text-red-800"
                          : "bg-gray-100 text-gray-800";
                        return (
                          <div className={`px-2 py-1 rounded-full text-xs font-medium ${tone}`}>
                            {(raw === "completed" || raw === "created") && (
                              <Check className="mr-1 h-3 w-3 inline" />
                            )}
                            {label}
                          </div>
                        );
                      })()}
                    </div>
                    <CardDescription>
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {proposal.client_name}
                      </div>
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {proposal.is_shared ? "Shared" : "Created"}:{" "}
                        {formatDate(proposal.created_at)}
                      </div>
                      {proposal.is_shared && proposal.permission_level && (
                        <Badge
                          className={`text-xs ${getPermissionColor(
                            proposal.permission_level
                          )}`}
                        >
                          <UserCheck className="h-3 w-3 mr-1" />
                          {getPermissionDisplay(proposal.permission_level)}
                        </Badge>
                      )}
                      {proposal.expires_at && (
                        <div className="text-xs text-orange-600">
                          Expires: {formatDate(proposal.expires_at)}
                        </div>
                      )}
                    </div>
                  </CardContent>
                  <CardFooter className="flex justify-between gap-2 flex-wrap">
                    {(proposal.workflow_type === 'boost_policy' ? (proposal.current_step ?? 0) < 6 : proposal.status === 'draft') ? (
                      // In-progress: show Continue button
                      <Button
                        className="w-full gap-2 group"
                        size="sm"
                        onClick={() => handleDocumentClick(proposal)}
                      >
                        {proposal.workflow_type === 'create_policy' ? 'Continue Creating' : 'Continue Boosting'}
                        <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                      </Button>
                    ) : (
                      // Completed: show View + Edit + Delete
                      <div className="flex items-center gap-2 w-full">
                        <Link
                          className="flex-1"
                          to={proposal.workflow_type === 'boost_policy' ? `/revisr/brand-policy/preview?tab=preview&id=${proposal.id}` : `/revisr/policies/generated?id=${proposal.id}`}
                        >
                          <Button variant="outline" size="sm" className="w-full gap-1.5">
                            <Eye className="h-3.5 w-3.5" />
                            View
                          </Button>
                        </Link>
                        {(!proposal.is_shared || (proposal.permission_level && proposal.permission_level !== 'view')) && (
                          <Link
                            className="flex-1"
                            to={proposal.workflow_type === 'boost_policy' ? `/revisr/brand-policy/editor?id=${proposal.id}&tab=edit` : `/revisr/policies/generated?id=${proposal.id}`}
                          >
                            <Button variant="outline" size="sm" className="w-full gap-1.5">
                              <FileEdit className="h-3.5 w-3.5" />
                              Edit
                            </Button>
                          </Link>
                        )}
                        {!proposal.is_shared && isOrgAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10 px-2"
                            onClick={() => handleDeleteDocument(proposal.id, proposal.is_shared || false)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    )}
                  </CardFooter>
                </Card>
              ))}
            </div>
          ) : (
            // List View
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full min-w-[600px] border-collapse bg-white text-sm [&_tr]:border-primary/25">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-4 py-3 text-left font-medium">Title</th>
                    <th className="px-4 py-3 text-left font-medium">Status</th>
                    <th className="px-4 py-3 text-left font-medium">Type</th>
                    <th className="px-4 py-3 text-left font-medium">Date</th>
                    <th className="px-4 py-3 text-right font-medium">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedProposals.map((doc) => (
                    <tr key={doc.id} className="border-b hover:bg-muted/50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{doc.title}</span>
                          {doc.is_shared && (
                            <Share2 className="h-4 w-4 text-blue-500" />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {(() => {
                          if (doc.workflow_type === 'boost_policy') {
                            const state = getApprovalState(doc);
                            if (state === 'pending') {
                              return (
                                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                                  <Clock className="h-3 w-3" /> Pending approval
                                </span>
                              );
                            }
                            if (state === 'rejected') {
                              return (
                                <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                                  <ShieldAlert className="h-3 w-3" /> Rejected
                                </span>
                              );
                            }
                            if (state === 'approved') {
                              return (
                                <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                  <ShieldCheck className="h-3 w-3" /> Approved
                                </span>
                              );
                            }
                          }
                          const raw = doc.status || "draft";
                          const label =
                            raw === "pending_approval" ? "Pending approval"
                            : raw === "rejected" ? "Rejected"
                            : raw === "generated" ? "Generated"
                            : raw === "completed" || raw === "created" ? "Completed"
                            : raw === "draft" ? "Draft"
                            : raw.charAt(0).toUpperCase() + raw.slice(1);
                          const tone =
                            raw === "completed" || raw === "created" ? "bg-green-100 text-green-800"
                            : raw === "draft" || raw === "pending_approval" ? "bg-amber-100 text-amber-800"
                            : raw === "rejected" ? "bg-red-100 text-red-800"
                            : "bg-gray-100 text-gray-800";
                          return (
                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${tone}`}>
                              {(raw === "completed" || raw === "created") && (
                                <Check className="mr-1 h-3 w-3" />
                              )}
                              {label}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3">
                        {doc.is_shared ? (
                          <div className="space-y-1">
                            <Badge
                              className={`text-xs ${getPermissionColor(
                                doc.permission_level
                              )}`}
                            >
                              <UserCheck className="h-3 w-3 mr-1" />
                              {getPermissionDisplay(doc.permission_level)}
                            </Badge>
                            <div className="text-xs text-muted-foreground">
                              by {doc.shared_by_name}
                            </div>
                            {doc.share_source && (
                              <div className="flex items-center gap-1 text-xs text-blue-600">
                                {getShareSourceIcon(doc.share_source)}
                                {getShareSourceDisplay(
                                  doc.share_source,
                                  doc.share_source_name
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <Badge className="text-xs bg-primary/10 text-primary">
                            <FileEdit className="h-3 w-3 mr-1" />
                            Owner
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          <div>
                            <div>{formatDate(doc.created_at)}</div>
                            {doc.expires_at && (
                              <div className="text-xs text-orange-600">
                                Expires: {formatDate(doc.expires_at)}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="min-h-[44px] min-w-[44px]" data-testid="document-actions">
                              <MoreHorizontal className="h-4 w-4" />
                              <span className="sr-only">Actions</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {(doc.workflow_type === 'boost_policy' ? (doc.current_step ?? 0) < 6 : doc.status === 'draft') ? (
                              <DropdownMenuItem
                                onClick={() => handleDocumentClick(doc)}
                              >
                                <ArrowRight className="mr-2 h-4 w-4" />
                                {doc.workflow_type === 'create_policy' ? 'Continue Creating' : 'Continue boosting'}
                              </DropdownMenuItem>
                            ) : (
                              <>
                                <DropdownMenuItem asChild>
                                  <Link
                                    to={doc.workflow_type === 'boost_policy' ? `/revisr/brand-policy/preview?tab=preview&id=${doc.id}` : `/revisr/policies/generated?id=${doc.id}`}
                                  >
                                    <Eye className="mr-2 h-4 w-4" />
                                    View
                                  </Link>
                                </DropdownMenuItem>
                                {(!doc.is_shared ||
                                  (doc.permission_level &&
                                    doc.permission_level !== "view")) && (
                                    <DropdownMenuItem asChild>
                                      <Link
                                        to={doc.workflow_type === 'boost_policy' ? `/revisr/brand-policy/editor?id=${doc.id}&tab=edit` : `/revisr/policies/generated?id=${doc.id}`}
                                      >
                                        <FileEdit className="mr-2 h-4 w-4" />
                                        Edit
                                      </Link>
                                    </DropdownMenuItem>
                                  )}
                              </>
                            )}

                            {/* {!doc.is_shared && doc.status !== 'draft' && (
                              <DropdownMenuItem
                                onClick={() => handleShareDocument(doc)}
                              >
                                <Users className="mr-2 h-4 w-4" />
                                Share
                              </DropdownMenuItem>
                            )} */}

                            {!doc.is_shared && (
                              <>
                                {/* Export options — boost-policy docs export the branded
                                    editor JSON from the DB; only once finished (step >= 6).
                                    Gated for members until the policy is approved (approval workflow). */}
                                {doc.workflow_type === 'boost_policy' && (doc.current_step ?? 0) >= 6 && isApprovalGatePassed(approvalRequired, isOrgAdmin, getApprovalState(doc)) && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => handleExportDoc(doc, 'pdf')}
                                      disabled={exportingDoc?.id === doc.id}
                                    >
                                      {exportingDoc?.id === doc.id && exportingDoc.format === 'pdf' ? (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                      ) : (
                                        <FileText className="mr-2 h-4 w-4" />
                                      )}
                                      {exportingDoc?.id === doc.id && exportingDoc.format === 'pdf' ? 'Exporting…' : 'Export as PDF'}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => handleExportDoc(doc, 'word')}
                                      disabled={exportingDoc?.id === doc.id}
                                    >
                                      {exportingDoc?.id === doc.id && exportingDoc.format === 'word' ? (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                      ) : (
                                        <FileDown className="mr-2 h-4 w-4" />
                                      )}
                                      {exportingDoc?.id === doc.id && exportingDoc.format === 'word' ? 'Exporting…' : 'Export as Word'}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => handleExportDoc(doc, 'html')}
                                      disabled={exportingDoc?.id === doc.id}
                                    >
                                      {exportingDoc?.id === doc.id && exportingDoc.format === 'html' ? (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                      ) : (
                                        <FileCode className="mr-2 h-4 w-4" />
                                      )}
                                      {exportingDoc?.id === doc.id && exportingDoc.format === 'html' ? 'Exporting…' : 'Export as HTML'}
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {isOrgAdmin && (
                                  <>
                                    <DropdownMenuSeparator />
                                    {/* <DropdownMenuItem
                                      onClick={() => handleRegenerateDocument(doc)}
                                    >
                                      <RefreshCw className="mr-2 h-4 w-4" />
                                      Regenerate
                                    </DropdownMenuItem> */}
                                    <DropdownMenuItem
                                      onClick={() =>
                                        handleDeleteDocument(
                                          doc.id,
                                          doc.is_shared || false
                                        )
                                      }
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      Delete
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination — appears once the filtered set exceeds a single page */}
          {!isLoading && totalItems > 0 && (
            <div className="flex flex-col gap-4 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <span>
                  Showing{" "}
                  <span className="font-medium text-foreground">{startIndex + 1}</span>
                  –
                  <span className="font-medium text-foreground">{endIndex}</span>{" "}
                  of{" "}
                  <span className="font-medium text-foreground">{totalItems}</span>{" "}
                  {totalItems === 1 ? "policy" : "policies"}
                </span>
                <div className="hidden items-center gap-2 sm:flex">
                  <span className="text-xs">Per page</span>
                  <Select
                    value={String(pageSize)}
                    onValueChange={(value) => setPageSize(Number(value))}
                  >
                    <SelectTrigger className="h-8 w-[72px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <SelectItem key={size} value={String(size)}>
                          {size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 w-9 p-0"
                    onClick={() => setCurrentPage(1)}
                    disabled={safePage === 1}
                    aria-label="First page"
                  >
                    <ChevronsLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 w-9 p-0"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>

                  {buildPageItems(safePage, totalPages).map((item, idx) =>
                    item === "ellipsis" ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="px-1.5 text-sm text-muted-foreground select-none"
                        aria-hidden="true"
                      >
                        …
                      </span>
                    ) : (
                      <Button
                        key={item}
                        variant={item === safePage ? "default" : "outline"}
                        size="sm"
                        className="h-9 min-w-[36px] px-2"
                        onClick={() => setCurrentPage(item)}
                        aria-label={`Page ${item}`}
                        aria-current={item === safePage ? "page" : undefined}
                      >
                        {item}
                      </Button>
                    )
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 w-9 p-0"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safePage === totalPages}
                    aria-label="Next page"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 w-9 p-0"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={safePage === totalPages}
                    aria-label="Last page"
                  >
                    <ChevronsRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Document Sharing Modal */}
      {selectedDocumentForSharing && (
        <DocumentSharingModal
          isOpen={isSharingModalOpen}
          onClose={() => {
            setIsSharingModalOpen(false);
            setSelectedDocumentForSharing(null);
          }}
          documentId={selectedDocumentForSharing.id}
          documentTitle={selectedDocumentForSharing.title}
          canManage={true}
        />
      )}
    </RevisrLayout>
  );
}
